import { Badge, type BadgeProps } from "@pericles/framework-frontend";
import { AI_REVIEW_STATUS, PRISMA_EXECUTION_STATUS, type AiReviewStatus, type ExecutionStatus } from "@pericles/shared";

import {
  AI_REVIEW_STATUS_UI,
  EXECUTION_STATUS_UI,
  normalizeAiReviewStatus,
  normalizeExecutionStatus,
} from "../domain/statusAdapters";

export type StatusBadgeMap = Record<string, { label: string; variant: BadgeProps["variant"] }>;

export const REVIEW_STATUS_BADGES: StatusBadgeMap = {
  draft: { label: "Draft", variant: "default" },
  pending_review: { label: "Pending review", variant: "warning" },
  in_review: { label: "In review", variant: "warning" },
  approved: { label: "Approved", variant: "success" },
  rejected: { label: "Rejected", variant: "danger" },
  archived: { label: "Archived", variant: "default" },
  // Legacy values kept for compatibility
  needs_changes: { label: "Needs changes", variant: "warning" },
  in_discussion: { label: "In discussion", variant: "warning" },
};

export const AI_REVIEW_STATUS_BADGES: StatusBadgeMap = {
  ...AI_REVIEW_STATUS_UI,
  // Legacy values kept for compatibility
  accepted_as_is: AI_REVIEW_STATUS_UI[AI_REVIEW_STATUS.completed],
  edited: AI_REVIEW_STATUS_UI[AI_REVIEW_STATUS.in_progress],
};

export const EXECUTION_STATUS_BADGES: StatusBadgeMap = {
  ...EXECUTION_STATUS_UI,
  // Legacy values kept for compatibility
  draft: EXECUTION_STATUS_UI[PRISMA_EXECUTION_STATUS.planned],
  not_started: EXECUTION_STATUS_UI[PRISMA_EXECUTION_STATUS.planned],
  cancelled: EXECUTION_STATUS_UI[PRISMA_EXECUTION_STATUS.dropped],
  canceled: EXECUTION_STATUS_UI[PRISMA_EXECUTION_STATUS.dropped],
};

function humanize(value?: string): string {
  if (!value) return "";
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function renderStatusBadge(status: string | undefined, map: StatusBadgeMap): JSX.Element | string {
  if (!status) {
    return "";
  }

  const config = map[status];
  const label = config?.label ?? humanize(status);
  const variant = config?.variant ?? "default";

  return <Badge label={label} variant={variant} />;
}

export interface RequirementReviewStatusBadgeProps {
  status?: string | null;
  className?: string;
}

export function RequirementReviewStatusBadge({ status, className }: RequirementReviewStatusBadgeProps): JSX.Element {
  return <div className={className}>{renderStatusBadge(status ?? undefined, REVIEW_STATUS_BADGES)}</div>;
}

export function AiReviewStatusBadge({ status, className }: { status?: AiReviewStatus | null; className?: string }): JSX.Element {
  return (
    <div className={className}>
      {renderStatusBadge(normalizeAiReviewStatus(status) ?? undefined, AI_REVIEW_STATUS_BADGES)}
    </div>
  );
}

export function ExecutionStatusBadge({
  status,
  className,
}: {
  status?: ExecutionStatus | string | null;
  className?: string;
}): JSX.Element {
  return (
    <div className={className}>
      {renderStatusBadge(normalizeExecutionStatus(status) ?? undefined, EXECUTION_STATUS_BADGES)}
    </div>
  );
}
